/**
 * Attachment type that supports both API format and local/form format.
 * - API format: `urls.small`, `urls.original` for different sizes
 * - Local/form format: `url` for temporary preview URLs
 * - `_destroy`: marks attachment for deletion (excluded from active attachments)
 */
export interface AttachmentWithUrls {
  urls?: {
    small?: string;
    medium?: string;
    original?: string;
  };
  url?: string;
  _destroy?: boolean;
}

/**
 * Finds the first active attachment (not marked for destruction) and returns its URL.
 * Handles both API format (urls.small/original) and local/form format (url).
 *
 * URL Priority:
 * 1. urls.small - optimized smaller size (preferred)
 * 2. urls.original - full-size fallback
 * 3. url - local/temporary URL (for form previews)
 *
 * @param attachments - Array of attachments from widget data
 * @returns The URL string or null if no active attachment found
 *
 * @example
 * // API format
 * getFirstActiveAttachmentUrl([{ urls: { small: 'https://...', original: 'https://...' } }])
 * // Returns: 'https://...' (small)
 *
 * @example
 * // Local form format
 * getFirstActiveAttachmentUrl([{ url: 'blob:...' }])
 * // Returns: 'blob:...'
 *
 * @example
 * // Destroyed attachment (skipped)
 * getFirstActiveAttachmentUrl([{ urls: { small: 'https://...' }, _destroy: true }])
 * // Returns: null
 */
export default function getFirstActiveAttachmentUrl(
  attachments: AttachmentWithUrls[] | null | undefined,
): string | null {
  if (!attachments || !Array.isArray(attachments)) {
    return null;
  }

  // Find first attachment that is not marked for destruction
  // eslint-disable-next-line no-underscore-dangle
  const activeAttachment = attachments.find((att) => !att?._destroy);

  if (!activeAttachment) {
    return null;
  }

  // Priority: urls.small > urls.original > url (local format)
  return (
    activeAttachment.urls?.small ||
    activeAttachment.urls?.original ||
    activeAttachment.url ||
    null
  );
}
